import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import GamePage from "./pages/GamePage";
import CustomGame from "./pages/CustomGame";
import HomePage from "./pages/HomePage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import UserProfile from "./pages/UserProfile";
import SearchPage from "./pages/SearchPage";
import YourLibrary from "./pages/YourLibrary";
import UserProfileCustomizer from "./pages/UserProfileCustomize";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import YourLibraryMobile from "./pages/mobile/YourLibraryMobile";
import ManageLibrary from "./pages/ManageLibrary";
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollProgressBar from './components/ScrollProgressBar';
import CursorEffect from './components/CursorEffect';

import loadingGif from './assets/images/loading.gif'

import { auth } from "./firebase/fireAuth";
import { useAuth } from "./hooks/useAuth";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

/* 🔒 Protected wrapper that respects auth loading */
function ProtectedRoute({ user, authLoading, children }) {
  if (authLoading) {
    return <div className="page-loading-state-con">
      <img src={loadingGif}></img>
      <p>Loading Page...</p>
      </div>;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return children;
}

function App() {
  const { user, authLoading } = useAuth();

  if (authLoading) {
    return <div className="page-loading-state-con">
      <img src={loadingGif}></img>
      <p>Loading Page...</p>
      </div>;
  }

  return (
    <BrowserRouter>
      <CursorEffect />
      <ScrollProgressBar />
      <ScrollToTop />
      <Header />
        <Routes>
          {/* ✅ Public routes */}
          <Route path="/" element={<HomePage user={user} />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/game/:gameId" element={<GamePage auth={auth} />} />
          <Route path="/game/:gameId/:slug" element={<GamePage auth={auth} />} />
          <Route path="/custom-game" element={<CustomGame auth={auth} />} />
          <Route path="/signup" element={<SignUpPage auth={auth} />} />
          <Route path="/signin" element={<SignInPage auth={auth}/>} />

          {/* 🔒 Protected routes */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute user={user} authLoading={authLoading}>
                <UserProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile/customize"
            element={
              <ProtectedRoute user={user} authLoading={authLoading}>
                <UserProfileCustomizer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/library"
            element={
              <ProtectedRoute user={user} authLoading={authLoading}>
                <YourLibrary />
              </ProtectedRoute>
            }
          />

          <Route
            path="/library/manage"
            element={
              <ProtectedRoute user={user} authLoading={authLoading}>
                <ManageLibrary />
              </ProtectedRoute>
            }
          />

          <Route
            path="/account-settings"
            element={
              <ProtectedRoute user={user} authLoading={authLoading}>
                <AccountSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* DEV */}
          <Route path="/dev/mobile-library" element={<YourLibraryMobile />} />

          {/* Optional catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      <Footer />
    </BrowserRouter>
  );
}

export default App;
